import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { LoadingService } from '../services/loading.service';

export const permittedRouteGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthenticationService);
  const loadingService = inject(LoadingService);

  loadingService.showLoader();

  try {
    // Ensure user is logged in and policy is loaded before proceeding
    const isLoggedIn = await authService.isLoggedIn();

    if (!isLoggedIn) {
      return redirectToLogin(router, loadingService);
    }

    const userPolicy = authService.currentUserPolicy;

    if (!userPolicy) {
      console.warn('User is logged in but policy not available');
      return redirectToLogin(router, loadingService);
    }

    const currentRoute = extractRouteFromUrl(state.url);
    const routeToCheck = currentRoute === '' ? 'dashboard' : currentRoute;

    console.log(`Checking route permission: ${routeToCheck}`, {
      userMenuItems: userPolicy.menuItems,
      isPermitted: isRoutePermitted(routeToCheck, userPolicy.menuItems)
    });

    // Check if the current route is permitted for this user
    if (isRoutePermitted(routeToCheck, userPolicy.menuItems)) {
      loadingService.hideLoader();
      return true; // User is authenticated and route is permitted, stay on current route
    }

    // User is authenticated but doesn't have permission for this route OR route doesn't exist
    // Redirect to their first permitted route
    return redirectToFirstPermittedRoute(router, loadingService, userPolicy.menuItems, currentRoute);

  } catch (error) {
    console.error('Error in permitted route guard:', error);
    return redirectToLogin(router, loadingService);
  }
};

// Utility functions to get the first route from the url
const extractRouteFromUrl = (url: string): string => {
  return url.split('?')[0].slice(1).split('/')[0];
};


const isRoutePermitted = (route: string, menuItems: string[]): boolean => {
  return menuItems.includes(route);
};

const redirectToLogin = async (router: Router, loadingService: LoadingService) => {
  console.warn('No user policy found, redirecting to login');
  loadingService.hideLoader();
  await router.navigate(['/login']);
  return false;
};

const redirectToFirstPermittedRoute = async (router: Router, loadingService: LoadingService, menuItems: string[], attemptedRoute: string) => {
  console.warn(`Route '${attemptedRoute}' is not permitted for user. Redirecting to first permitted route.`);
  const firstPermittedRoute = menuItems[0];
  loadingService.hideLoader();
  await router.navigate([`/${firstPermittedRoute}`]);
  return false;
};
